"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "../lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Opt-in escape hatch for a tooltip whose trigger sits inside a `<button>` (or
 * any other element whose content model rejects a `<div>`): wrap the
 * `TooltipContent` in this and the content renders at the end of `<body>`
 * instead of next to the trigger. Left opt-in rather than folded into
 * `TooltipContent` because portalling every tooltip in the app is a far wider
 * change than any one caller needs — see the note on `font-normal` below for
 * what the un-portalled default costs.
 */
const TooltipPortal = TooltipPrimitive.Portal;

/**
 * Lets `TooltipTrigger` flip the open state its own `Tooltip` owns. Null when a
 * trigger is used outside a `Tooltip` — nobody does that, but it must not throw.
 */
const TooltipTouchContext = React.createContext<{ toggle: () => void } | null>(
  null,
);

/**
 * Radix drives tooltips from hover and focus and deliberately ignores touch —
 * per WAI-ARIA, a tooltip is not a tap target. On a phone that left every
 * tooltip in the app unreachable: there is no hover to perform, so the content
 * behind one (a vehicle's brand/model, why a send is unavailable) simply could
 * not be read (P-2683).
 *
 * So `Tooltip` owns the open state and `TooltipTrigger` toggles it on a touch
 * tap. Hover and focus still go through Radix untouched — the extra branch is
 * `pointerType === "touch"` only, so nothing about the desktop behaviour moves.
 * `open`/`onOpenChange` keep working for callers that drive it themselves.
 */
const Tooltip = ({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const touch = React.useMemo(
    () => ({ toggle: () => setOpen(!isOpen) }),
    [setOpen, isOpen],
  );

  return (
    <TooltipTouchContext.Provider value={touch}>
      <TooltipPrimitive.Root {...props} open={isOpen} onOpenChange={setOpen} />
    </TooltipTouchContext.Provider>
  );
};

Tooltip.displayName = "Tooltip";

/**
 * How long after a touch toggle the click that tap synthesises may still
 * arrive. It normally follows within a frame or two; legacy mobile browsers
 * add up to ~300 ms of tap delay. Anything later is a different gesture.
 */
const TOUCH_CLICK_WINDOW_MS = 1000;

const TooltipTrigger = ({
  onPointerDown,
  onPointerCancel,
  onClick,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) => {
  const touch = React.useContext(TooltipTouchContext);
  /**
   * When the last tap toggled this tooltip, so the click that tap synthesises
   * can be told apart from any later one. A plain boolean latch stayed set for
   * good whenever the tap produced no click on this trigger — the finger
   * scrolls away (`pointercancel`), or lifts off the trigger so the click
   * lands on an ancestor — and then swallowed the next real click, including
   * the synthetic one Enter/Space fires on a focused trigger. For an `asChild`
   * child with a native default action (a link, a submit button) that action
   * would silently never run.
   */
  const touchToggledAt = React.useRef<number | null>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event);
    if (event.defaultPrevented || event.pointerType !== "touch") return;

    // Radix composes handlers with `checkForDefaultPrevented`, so preventing
    // the default here is what stops its own pointerdown handler — which closes
    // the tooltip — from undoing the toggle below.
    event.preventDefault();
    touchToggledAt.current = event.timeStamp;
    touch?.toggle();
  };

  /** A cancelled tap — the finger scrolled away — synthesises no click. */
  const handlePointerCancel = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    onPointerCancel?.(event);
    touchToggledAt.current = null;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);

    // Disarm on every click, so at most one is ever suppressed per tap.
    const toggledAt = touchToggledAt.current;
    touchToggledAt.current = null;
    if (
      toggledAt === null ||
      event.timeStamp - toggledAt > TOUCH_CLICK_WINDOW_MS
    ) {
      return;
    }

    // A tap fires pointerdown → pointerup → click, and Radix's trigger closes
    // the tooltip on click. Without this the toggle above would be undone
    // within the same tap. The consumer's own `onClick` has already run, and
    // `preventDefault` does not stop it — it only tells Radix to stand down.
    event.preventDefault();
  };

  return (
    <TooltipPrimitive.Trigger
      {...props}
      onPointerDown={handlePointerDown}
      onPointerCancel={handlePointerCancel}
      onClick={handleClick}
    />
  );
};

TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = ({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Content
    sideOffset={sideOffset}
    className={cn(
      // font-normal is pinned for the same reason Button pins font-sans:
      // TooltipContent is not portalled, so it renders inside its trigger's
      // parent and inherited whatever weight that parent carried — a tooltip
      // hung off a FormLabel (font-medium) came out Medium. PDS tooltip copy
      // is body text, i.e. Regular.
      "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm font-normal text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className,
    )}
    {...props}
  />
);

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
};
